/**
 * Admin Layout Component
 * 
 * Shared responsive layout for all admin pages with sidebar, header with profile dropdown, and footer
 * Implements role-based access control for navigation items
 */

import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/utils/auth';
import apiClient from '@/utils/api';
import {
  LayoutDashboard,
  Users,
  Clock,
  FileText,
  Settings,
  LogOut,
  CreditCard,
  Menu,
  FolderOpen,
  User,
  Edit,
  ChevronDown,
  Shield,
  UserCog,
  Building2,
  Activity
} from 'lucide-react';

// Define nav items with role restrictions
const allNavItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/admin', roles: ['super_admin', 'admin'] },
  { id: 'members', label: 'Members', icon: Users, path: '/admin/members', roles: ['super_admin', 'admin'] },
  { id: 'approvals', label: 'Approvals', icon: Clock, path: '/admin/approvals', roles: ['super_admin', 'admin'] },
  { id: 'beneficiaries', label: 'Beneficiaries', icon: Shield, path: '/admin/beneficiaries', roles: ['super_admin', 'admin'] },
  { id: 'payments', label: 'Payments', icon: CreditCard, path: '/admin/payments', roles: ['super_admin', 'admin'] },
  { id: 'providers', label: 'Providers', icon: Building2, path: '/admin/providers', roles: ['super_admin', 'admin'] },
  { id: 'documents', label: 'Documents', icon: FolderOpen, path: '/admin/documents', roles: ['super_admin', 'admin'] },
  { id: 'reports', label: 'Reports', icon: FileText, path: '/admin/reports', roles: ['super_admin', 'admin'] },
  { id: 'usage', label: 'Usage & Activity', icon: Activity, path: '/admin/usage', roles: ['super_admin'] },
  { id: 'users', label: 'Users', icon: UserCog, path: '/admin/users', roles: ['super_admin'] },
  { id: 'settings', label: 'Settings', icon: Settings, path: '/admin/settings', roles: ['super_admin'] },
];

// Filter nav items based on user role
const getNavItemsForRole = (role) => {
  return allNavItems.filter(item => item.roles.includes(role));
};

// Get role display name and badge color
const getRoleDisplay = (role) => {
  switch (role) {
    case 'super_admin':
      return { label: 'Super Admin', color: 'bg-purple-100 text-purple-800' };
    case 'admin':
      return { label: 'Administrator', color: 'bg-blue-100 text-blue-800' };
    default:
      return { label: 'Member', color: 'bg-gray-100 text-gray-800' };
  }
};

const NavContent = ({ currentPage, onItemClick, userRole, pendingPaymentsCount, pendingBeneficiariesCount, pendingApprovalsCount }) => {
  const navItems = getNavItemsForRole(userRole || 'admin');
  
  return (
    <nav className="space-y-1">
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = currentPage === item.id;
        const showPaymentsBadge = item.id === 'payments' && pendingPaymentsCount > 0;
        const showBeneficiariesBadge = item.id === 'beneficiaries' && pendingBeneficiariesCount > 0;
        const showApprovalsBadge = item.id === 'approvals' && pendingApprovalsCount > 0;
        
        let badgeCount = 0;
        let badgeColor = 'bg-red-500 text-white';
        let badgeTestId = '';
        
        if (showPaymentsBadge) {
          badgeCount = pendingPaymentsCount;
          badgeColor = 'bg-red-500 text-white';
          badgeTestId = 'pending-payments-badge';
        } else if (showBeneficiariesBadge) {
          badgeCount = pendingBeneficiariesCount;
          badgeColor = 'bg-amber-500 text-white';
          badgeTestId = 'pending-beneficiaries-badge';
        } else if (showApprovalsBadge) {
          badgeCount = pendingApprovalsCount;
          badgeColor = 'bg-blue-500 text-white';
          badgeTestId = 'pending-approvals-badge';
        }
        
        const showBadge = showPaymentsBadge || showBeneficiariesBadge || showApprovalsBadge;
        
        return (
          <Link
            key={item.id}
            to={item.path}
            onClick={onItemClick}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
            data-testid={`nav-${item.id}`}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium flex-1">{item.label}</span>
            {showBadge && (
              <span 
                className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                  isActive ? 'bg-white text-primary' : badgeColor
                }`}
                data-testid={badgeTestId}
              >
                {badgeCount > 99 ? '99+' : badgeCount}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
};

export const AdminSidebar = ({ currentPage, branding, userRole, pendingPaymentsCount, pendingBeneficiariesCount, pendingApprovalsCount }) => {
  return (
    <aside className="hidden lg:flex w-64 bg-card border-r border-border sticky top-0 h-screen overflow-y-auto p-6 flex-col">
      <div className="flex items-center gap-3 mb-6">
        {branding?.logo_url && branding?.use_logo_in_header ? (
          <img src={branding.logo_url} alt={branding?.app_name || 'Logo'} className="h-8 w-auto" />
        ) : (
          <h2 className="text-xl font-heading font-bold text-foreground">
            {branding?.app_name || 'Admin Panel'}
          </h2>
        )}
      </div>
      <div className="flex-1">
        <NavContent currentPage={currentPage} userRole={userRole} pendingPaymentsCount={pendingPaymentsCount} pendingBeneficiariesCount={pendingBeneficiariesCount} pendingApprovalsCount={pendingApprovalsCount} />
      </div>
    </aside>
  );
};

export const MobileNav = ({ currentPage, branding, userRole, pendingPaymentsCount, pendingBeneficiariesCount, pendingApprovalsCount }) => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const roleDisplay = getRoleDisplay(userRole);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
          <Menu className="h-6 w-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 p-0">
        <div className="flex flex-col h-full">
          <div className="p-6 border-b">
            <div className="flex items-center gap-3">
              {branding?.logo_url && branding?.use_logo_in_header ? (
                <img src={branding.logo_url} alt={branding?.app_name || 'Logo'} className="h-8 w-auto" />
              ) : (
                <h2 className="text-xl font-heading font-bold">{branding?.app_name || 'Admin Panel'}</h2>
              )}
            </div>
          </div>
          <div className="flex-1 p-4 overflow-y-auto">
            <NavContent currentPage={currentPage} onItemClick={() => setOpen(false)} userRole={userRole} pendingPaymentsCount={pendingPaymentsCount} pendingBeneficiariesCount={pendingBeneficiariesCount} pendingApprovalsCount={pendingApprovalsCount} />
          </div>
          <div className="p-4 border-t">
            <div className="mb-3 px-4">
              <p className="text-sm font-medium">{user?.name || 'Admin'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${roleDisplay.color}`}>
                {roleDisplay.label}
              </span>
            </div>
            <div className="space-y-2">
              <Link to="/admin/profile" onClick={() => setOpen(false)}>
                <Button variant="outline" className="w-full justify-start">
                  <User className="w-4 h-4 mr-2" />
                  My Profile
                </Button>
              </Link>
              <Button variant="outline" className="w-full justify-start text-destructive hover:text-destructive" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const AdminHeader = ({ title, currentPage, branding, userRole, pendingPaymentsCount, pendingBeneficiariesCount, pendingApprovalsCount }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const roleDisplay = getRoleDisplay(userRole);
  const initials = (user?.name || 'A').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <header className="bg-card border-b border-border py-3 px-4 md:py-4 md:px-8 flex justify-between items-center sticky top-0 z-40">
      <div className="flex items-center gap-3">
        <MobileNav currentPage={currentPage} branding={branding} userRole={userRole} pendingPaymentsCount={pendingPaymentsCount} pendingBeneficiariesCount={pendingBeneficiariesCount} pendingApprovalsCount={pendingApprovalsCount} />
        <h1 className="text-lg md:text-2xl font-heading font-bold text-foreground truncate">{title}</h1>
      </div>
      <div className="flex items-center gap-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2" data-testid="admin-profile-dropdown">
              {user?.picture ? (
                <img src={user.picture} alt="Profile" className="w-8 h-8 rounded-full object-cover" data-testid="admin-avatar-image" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary" data-testid="admin-avatar-initials">
                  {initials}
                </div>
              )}
              <span className="hidden sm:block text-sm text-left leading-tight">
                <span className="font-medium block">{user?.name || 'Admin'}</span>
                <span className="text-xs text-muted-foreground">{roleDisplay.label}</span>
              </span>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.name || 'Admin'}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
              <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${roleDisplay.color}`}>
                {roleDisplay.label}
              </span>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/admin/profile" className="cursor-pointer" data-testid="admin-menu-profile">
                <User className="w-4 h-4 mr-2" />
                My Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/admin/profile/edit" className="cursor-pointer" data-testid="admin-menu-edit-profile">
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout} 
              className="text-destructive focus:text-destructive cursor-pointer"
              data-testid="admin-menu-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export const AdminFooter = ({ branding }) => {
  const currentYear = new Date().getFullYear();
  const footerHtml = branding?.footer_html;
  
  if (footerHtml) {
    return (
      <footer 
        className="bg-card border-t border-border py-4 px-4 md:px-8 text-center text-sm text-muted-foreground"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(footerHtml) }}
      />
    );
  }
  
  return (
    <footer className="bg-card border-t border-border py-4 px-4 md:px-8 text-center text-sm text-muted-foreground">
      <p>© {currentYear} {branding?.app_name || 'MemberHub'}. All rights reserved.</p>
    </footer>
  );
};

const AdminLayout = ({ children, currentPage, title }) => {
  const [branding, setBranding] = useState(null);
  const [userRole, setUserRole] = useState('admin');
  const [pendingPaymentsCount, setPendingPaymentsCount] = useState(0);
  const [pendingBeneficiariesCount, setPendingBeneficiariesCount] = useState(0);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);

  useEffect(() => {
    const loadBranding = async () => {
      try {
        const response = await apiClient.get('/settings/branding');
        setBranding(response.data);
      } catch (error) {
        // Use defaults if branding not available
        setBranding({ app_name: 'MemberHub' });
      }
    };

    const loadUserRole = async () => {
      try {
        const response = await apiClient.get('/admin/users/me/role');
        setUserRole(response.data.role);
      } catch (error) {
        // Default to admin if can't fetch role
        setUserRole('admin');
      }
    };

    const loadPendingPaymentsCount = async () => {
      try {
        const response = await apiClient.get('/payments/pending-count');
        setPendingPaymentsCount(response.data.count || 0);
      } catch (error) {
        // Default to 0 if can't fetch count
        setPendingPaymentsCount(0);
      }
    };

    const loadPendingBeneficiariesCount = async () => {
      try {
        const response = await apiClient.get('/members/admin/pending-count');
        setPendingBeneficiariesCount(response.data.pending_count || 0);
      } catch (error) {
        // Default to 0 if can't fetch count
        setPendingBeneficiariesCount(0);
      }
    };

    const loadPendingApprovalsCount = async () => {
      try {
        const response = await apiClient.get('/admin/applications/pending-count');
        setPendingApprovalsCount(response.data.count || 0);
      } catch (error) {
        // Default to 0 if can't fetch count
        setPendingApprovalsCount(0);
      }
    };

    loadBranding();
    loadUserRole();
    loadPendingPaymentsCount();
    loadPendingBeneficiariesCount();
    loadPendingApprovalsCount();
    
    // Refresh counts every 30 seconds
    const interval = setInterval(() => {
      loadPendingPaymentsCount();
      loadPendingBeneficiariesCount();
      loadPendingApprovalsCount();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar currentPage={currentPage} branding={branding} userRole={userRole} pendingPaymentsCount={pendingPaymentsCount} pendingBeneficiariesCount={pendingBeneficiariesCount} pendingApprovalsCount={pendingApprovalsCount} />
      <div className="flex-1 flex flex-col min-w-0">
        <AdminHeader title={title} currentPage={currentPage} branding={branding} userRole={userRole} pendingPaymentsCount={pendingPaymentsCount} pendingBeneficiariesCount={pendingBeneficiariesCount} pendingApprovalsCount={pendingApprovalsCount} />
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-x-hidden">
          {children}
        </main>
        <AdminFooter branding={branding} />
      </div>
    </div>
  );
};

export default AdminLayout;
